<script setup lang='ts'>
import {computed, onMounted, ref, watch, onUnmounted} from 'vue';
import FlightOptions from "@/components/FlightOptions/FlightOptions.vue";
import CustomModal from "@/components/CustomModal/CustomModal.vue";
import {useRoute, useRouter} from "vue-router";
import { useWindowSize } from '@vueuse/core';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import ImageGallery from "@/components/ImageGallery/ImageGallery.vue";
import {useI18n} from 'vue-i18n';
import {useFormatStore} from "@/stores/formatStore";
import LocationPin from "@/components/Map/LocationPin.vue";
import {useYachtStore} from "@/stores/yachtStore";
import YachtBookingControl from "@/pages/YachtPage/YachtBookingControl.vue";
import FAQ from "@/components/FAQ/FAQ.vue";
import {storeToRefs} from "pinia";
import fallbackImage from '@/assets/images/no_picture.png';
import CustomButton from "@/components/Button/Button.vue";
import AddToFavoriteButton from "@/components/Button/AddToFavoriteButton.vue";
import TripAddModal from "@/components/TripCard/TripAddModal.vue";
import {useTripsStore} from "@/stores/tripsStore";
import { useUserStore } from "@/stores/userStore";
import {VueSpinner} from "vue3-spinners";
import * as DateUtils from "@/helpers/dateUtils";
import { getAsset } from "@/helpers/assetsUtils";
import {Trip} from "@/models/Trip";
import YachtAttributes from "@/components/Yacht/YachtAttributes.vue";
import YachtOverview from "@/components/Yacht/YachtOverview.vue";
import YachtAmenities from "@/components/Yacht/YachtAmenities.vue";
import YachtGallery from "@/components/Yacht/YachtGallery.vue";
import YachtDateAndPassengers from "@/components/Yacht/YachtDateAndPassengers.vue";
import YachtSecurePayments from "@/components/Yacht/YachtSecurePayments.vue";
import YachtPricingSummary from "@/components/Yacht/YachtPricingSummary.vue";
import YachtStickyHeader from "@/components/Yacht/YachtStickyHeader.vue";
import {useLocation} from "@/composables/useLocation";
import YachtAvailabilityBar from "@/components/Yacht/YachtAvailabilityBar.vue";
import { bookTripOffer } from "@/services/trip.service";

const {t} = useI18n(); // Import the translation function
const route = useRoute();
const router = useRouter();
const formatStore = useFormatStore();
const yachtStore = useYachtStore();
const userStore = useUserStore();

const trip = ref<Trip | null>(null);

const showAddTripModal = ref(false);
const tripsStore = useTripsStore();

function closeMapPopup() {
  const mapOverlay = document.getElementById("map-overlay");
  if (mapOverlay) mapOverlay.classList.add("hidden");
}

const showFlightOptionsModal = ref(false);
const showImageGalleryModal = ref(false);
const windowCurrSize = ref();

function buildOfferPayload() {
  return {
    yachtId: yachtStore.yacht.id,
    name: yachtStore.yacht.name,
    basePrice:yachtStore.priceDetails.basePrice,
    discount: yachtStore.availabilities[1]?.discount,
    offerPrice: yachtStore.priceDetails.total, 
    dateStart: trip.value?.dateStart,
    dateEnd: trip.value?.dateEnd,
    destination: yachtStore.yacht?.location?.id ?? null,
    amenities: yachtStore.optionalAmenities,
    selectedAmenities: yachtStore.selectedAmenities,
    passengers: yachtStore.selections.persons
  };
}

function windowSize() {
  windowCurrSize.value = innerWidth;
}

const { width } = useWindowSize();
const isMobile = computed(() => width.value < 768);
const windowWidth = ref(window.innerWidth);


function updateWindowWidth() {
  windowWidth.value = window.innerWidth;
}


function mainImageCarousel(mainImage, altImages) {
  let result = [{id: 1999999, url: mainImage}, ...altImages];

  return result;
}

onUnmounted(() => {
  yachtStore.resetState()
  window.removeEventListener("resize", updateWindowWidth);
});

onMounted(async () => {
  
  const userStore = useUserStore();
  await userStore.restoreSession();
  
  yachtStore.handleRoute(route);

  // single watcher for selections -> fetch
  const { selections } = storeToRefs(yachtStore);
  watch(
      selections,
      () => {
        yachtStore.fetch();
      },
      { deep: true }
  );


  windowSize();
  window.addEventListener("resize", updateWindowWidth);
  window.addEventListener("resize", windowSize);
});

const createdSuccess = ref(false);
const isCreatingTrip = ref(false);
const { normalizeLocation } = useLocation();

async function handleAvailabilityBooking() {
  isCreatingTrip.value = true;
  try {
    const selectedLocation = ref<any[]>(normalizeLocation(yachtStore.yacht?.location ?? null) || []);
    let offer = buildOfferPayload();

    offer = {
      ...offer,
      dateStart: formatStore.formatDate(new Date(yachtStore.selections.dateFrom)),
      dateEnd: formatStore.formatDate(new Date(yachtStore.selections.dateTo))
    }

    const [entity, id] = selectedLocation.value?.[0]?.split(':') || [];
    const payload = {
      name: `${yachtStore.location?.name} - ${yachtStore.yacht.model?.name} - ${yachtStore.yacht?.year} @ ${DateUtils.format(yachtStore.selections.dateFrom)}`,
      dateStart: DateUtils.format(yachtStore.selections.dateFrom),
      dateEnd: DateUtils.format(yachtStore.selections.dateTo),
      locationId: id,
      locationType: 'Harbour' //We force it here because the availability does not provide anything.
    };

    console.log("Creating trip with payload (minimal):", payload);
    const tripRes = await tripsStore.createTrip(payload);
    const tripSlug = tripRes?.slug;
    console.log("CreateTrip response:", tripRes);

    if (!tripSlug) {
      throw new Error("Trip created but no slug returned");
    }

    await tripsStore.patchOfferToTrip(tripSlug, offer);
    console.log("Offer attached successfully (trip add modal)", offer);
    createdSuccess.value = true;
    await tripsStore.loadTrip(tripSlug);
    // Book the offer
    await bookTripOffer(tripSlug, tripsStore.trip.offers[0].uuid);
    
    await tripsStore.loadTrip(tripSlug);

    // Redirect to overview
    await router.push({
      name: 'trip-overview',
      params: {
        locale: route.params.locale,
        id: tripSlug
      }
    });
  } catch (error) {
    console.error("Error creating trip:", error);
  } finally {
    isCreatingTrip.value = false;
  }
}

const loginEnabled = import.meta.env.VITE_LOGIN_ENABLED


const selectedAmenitiesIds = computed({
  get() {
    // return only the ids
    return yachtStore.selectedAmenities.map(a => a.id);
  },
  set(ids: number[]) {
    // convert ids back to full objects
    yachtStore.selectedAmenities = yachtStore.optionalAmenities
        .filter(a => ids.includes(Number(a.id)))
        .map(a => ({ ...a, id: Number(a.id) })); // ensure id is number
  }
});

</script>

<template>

  <!-- Initial page loader -->
  <div
      v-if="!yachtStore.yacht"
      class="flex justify-center items-center min-h-[600px]"
  >
    <VueSpinner size="40" color="#1B84FF" />
  </div>
  
  <CustomModal :customIndex="1000" :show="showImageGalleryModal" style="z-index: 100 !important" :mobileClose="true"
               extraClasses="gallery-popup h-[85vh] md:h-[95vh] w-[96vw] overflow-hidden"
               @close="showImageGalleryModal = false">
    <image-gallery
        :images="mainImageCarousel(yachtStore.yacht.mainImage.url || yachtStore.yacht.mainImage.name || fallbackImage, yachtStore.yacht.images)"
        @close="showImageGalleryModal = false"/>
  </CustomModal>

  <!--  On scroll -->
  <YachtStickyHeader
      :yacht="yachtStore.yacht"
      :availability="yachtStore.availabilities[1]"
      :price-details="yachtStore.priceDetails"
      :selected-dates-text="yachtStore.selectedAvailabilityDatesText"
      :selections="yachtStore.selections"
  >
    <template #booking-control>
      <YachtBookingControl :location="yachtStore.location" />
    </template>
  </YachtStickyHeader>
  
  <div
      class="main-yacht-page-container flex flex-col mt-28"
      v-if="yachtStore.yacht"
  >

    <div class="mb-6 container">
      <CustomButton
          type="outline-black"
          :outlined="true"
          :rounded="true"
          :icon="getAsset('icons', 'Arrow_left_16x16.svg')"
          :hoverIcon="getAsset('icons', 'Arrow_left_white_16x16.svg')"
          @click="router.go(-1)"
      >
        {{ t('goback') }}
      </CustomButton>
    </div>
    <!-- Overview Section -->
    <section id="overview">
      <div class="boat-data-header container border rounded-8xl">
        <!--      Header Top-->
        <div class="boat-data-header-top min-h-[350px] pt-11 pb-8 gap-4 flex justify-between bg-gradient-to-r rounded-8xl from-gradients-ghostWhite to-gradients-aliceBlue">
          

          <div class="flex justify-end px-6" v-if="isMobile">
            <AddToFavoriteButton
                v-if="loginEnabled === 'true'"
                v-model:show-add-trip-modal="showAddTripModal"
                :yacht="{ yacht: yachtStore.yacht, price: yachtStore.priceDetails.basePrice, finalPrice: yachtStore.priceDetails.total  }"
                :date-from="yachtStore.selections.dateFrom"
                :date-to="yachtStore.selections.dateTo"
                :yacht-location="yachtStore.yacht?.location?.id ?? null"
                :amenities="yachtStore.optionalAmenities"
                :selected-amenities="yachtStore.selectedAmenities"
                :passengers="yachtStore.selections.persons"
                :discount="yachtStore.availabilities[1]?.discount"
                popup-size="medium"
                class="w-[50px] h-[30px] border rounded-full flex items-center justify-center"
            />
          </div>
          <div class="boat-data-header-left px-6 sm:pl-10 sm:pr-6 mb-2 flex-1 flex flex-col gap-4 w-2/3">
            <!-- Top content -->
            <div class="flex flex-col gap-3">
              <LocationPin v-model="yachtStore.yachtLocation" class="location flex gap-1" />
              <div class="boat-name flex align-center gap-x-4">
                <div class="boat-name__text responsive-title-3 font-bold" v-if="yachtStore.yacht">
                  {{ yachtStore.yacht.model?.name }} · {{ yachtStore.yacht.year }} | {{ yachtStore.yacht.name }}
                </div>
              </div>
            </div>

            <YachtDateAndPassengers
                v-model:dateFrom="yachtStore.selections.dateFrom"
                v-model:dateTo="yachtStore.selections.dateTo"
                v-model:passengers="yachtStore.selections.persons"
                :passengers-options="yachtStore.passengers"
            />
            
            <div class="flex-grow"></div>
            
            <!-- Secure Payments -->
            <YachtSecurePayments/>

          </div>
          <div class="boat-data-header-right sm:py-11 px-6 sm:pr-10 sm:pl-6 flex flex-col gap-y-4 relative w-1/3">
            <div class="flex justify-end" v-if="!isMobile">
              <AddToFavoriteButton
                  v-if="loginEnabled === 'true'"
                  v-model:show-add-trip-modal="showAddTripModal"
                  :yacht="{ yacht: yachtStore.yacht, price: yachtStore.priceDetails.basePrice, finalPrice: yachtStore.priceDetails.total }"
                  :date-from="yachtStore.selections.dateFrom"
                  :date-to="yachtStore.selections.dateTo"
                  :yacht-location="yachtStore.yacht?.location?.id ?? null"
                  :amenities="yachtStore.optionalAmenities"
                  :selected-amenities="yachtStore.selectedAmenities"
                  :passengers="yachtStore.selections.persons"
                  :discount="yachtStore.availabilities[1]?.discount"
                  :popup-size="windowWidth < 1111 ? 'medium' : 'default'"
                  class="w-[50px] h-[30px] border rounded-full flex items-center justify-center"
              />
            </div>
            <YachtPricingSummary
                :availabilities="yachtStore.availabilities[1]"
                :price-details="yachtStore.priceDetails"
                :yacht="yachtStore.yacht"
                :date-days="yachtStore.dateDays"
                :loading="yachtStore.availabilities[1]?.state === 'loading'"
            >
              <template #button>
                <!-- User NOT logged in -> Request Quote -->
                <div class="mt-3" v-if="!userStore.isLoggedIn"><YachtBookingControl/></div>

                <!-- User IS logged in -->
                <div v-else>
                  <!-- Availability -->
                  <div class="mt-4">
                    <CustomButton type="black"
                                  :rounded="true"
                                  :outlined="false"
                                  class="w-100 my-0"
                                  @click="handleAvailabilityBooking"
                    >
                      <div v-if="isCreatingTrip" class="flex justify-center items-center">
                        <VueSpinner size="25" class="p-2" color="white"/>
                      </div>
                      <div v-else>{{ t('book') }}</div>
                    </CustomButton>
                  </div>
                </div>
              </template>
            </YachtPricingSummary>
          </div>
        </div>

        <!-- Header Bottom-->
        <YachtAvailabilityBar
            :availabilities="yachtStore.availabilities"
            :format-date="yachtStore.availabilityDatesText"
            :can-move-prev="yachtStore.canMoveToPreviousPeriod"
            :can-move-next="true"
            :is-mobile="isMobile"
            @prev="yachtStore.moveToPreviousPeriod"
            @next="yachtStore.moveToNextPeriod"
            @select="yachtStore.selectAvailability"
        />

      </div>
      
      <YachtOverview
          :yacht="yachtStore.yacht"
          :equipment="yachtStore.equipment"
          :fallbackImage="fallbackImage"
          @open-gallery="showImageGalleryModal = true"
      />

      <hr/>

      <YachtAttributes :yacht="yachtStore.yacht"/>
      

    </section>

    <!-- Gallery & Price Includes Section -->
    <YachtGallery
        :images="yachtStore.yacht.images"
        :is-mobile="isMobile"
        @openGallery="showImageGalleryModal = true"
    />

    <!-- Addons Section -->
    <YachtAmenities
        :mandatory-amenities="yachtStore.mandatoryAmenities"
        :optional-amenities="yachtStore.optionalAmenities"
        v-model:selected-amenities="selectedAmenitiesIds"
    />
    
    <!-- Things to Know Section -->
    <FAQ />

    <CustomModal :show="showFlightOptionsModal" @close="showFlightOptionsModal = false">
      <FlightOptions @close="showFlightOptionsModal = false"/>
    </CustomModal>

    <div id="map-overlay" class="map-popup-overlay hidden" @click="closeMapPopup">
      <div id="map-overlay-map" class="map-popup-map" @click.stop>
        <!-- Close Button -->
        <div
            @click="closeMapPopup"
            class="closemap-button cursor-pointer flex items-center justify-center z-50 absolute border border-black rounded-full p-1.5"
        >
          <img src="@/assets/icons/svg/Close_16x16.svg" alt="">
        </div>
      </div>
    </div>

  </div>


  <TripAddModal
      v-model:show="showAddTripModal"
      @on-trip-created="tripsStore.getAll"
      :date-from="yachtStore.selections.dateFrom"
      :date-to="yachtStore.selections.dateTo"
      :yacht-location="yachtStore.yacht?.location ?? null"
      :yacht="{ yacht: yachtStore.yacht, price: yachtStore.priceDetails.basePrice, finalPrice: yachtStore.priceDetails.total, discount: yachtStore.availabilities[1]?.discount, passengers: yachtStore.selections.persons }"
      :amenities="yachtStore.optionalAmenities"
      :selected-amenities="yachtStore.selectedAmenities"
  />
</template>

<style scoped>
:deep(.always-enabled .v-selection-control--disabled) {
  opacity: 1;
}

:deep(.always-enabled.v-input--horizontal) {
  grid-template-areas: unset !important;
}

.add-trip__btn:hover {
  border-color: #000 !important;
}

.flight-options-button:hover img {
  filter: brightness(0) invert(1);
}

.map-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 99;
  display: flex;
  align-items: center;
  justify-content: center;
}

.map-popup-map {
  width: 80%;
  height: 80%;
  background-color: white;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
}

.closemap-button {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 32px;
  height: 32px;
  background: white;
  border: 3px solid black;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.closemap-button img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.hidden {
  display: none;
}

@media (max-width: 1640px) {
  .boat-data-header-top .boat-data-header-left .location div {
    font-size: 0.6875rem !important;
  }

  .boat-data-header-top .boat-data-header-left .location img {
    height: 16px !important;
  }

  .boat-name .boat-name__text {
    font-size: 1.875rem;
  }

  .boat-data-header-left .date-subtitle,
  .boat-data-header-left .passengers-subtitle {
    font-size: .75rem;
  }

  .boat-data-header-left .date-content,
  .boat-data-header-left .passengers-content {
    font-size: 1.5rem;
  }

  .boat-data-header-right .header-title {
    font-size: 1.125rem;
  }

  .boat-data-header-right .boat-charges {
    font-size: 0.75rem;
  }

  .boat-data-header-right .total-text {
    font-size: .875rem;
  }

  .boat-data-header-right .final-price {
    font-size: 1.5rem;
  }
}

@media (max-width: 1538px) {
  
  .faq-title {
    font-size: 1.5rem;
  }

  :deep(.el-collapse-item .el-collapse-item__header) {
    font-size: 1.125rem;
    background: #f8f8f8;
  }
}

@media (max-width: 1450px) {
  .data-iconboxes img {
    height: 32px;
  }

  .data-iconboxes .iconbox__text {
    font-size: 1rem;
  }
}

@media (max-width: 1280px) {
  .boat-name .boat-name__text {
    font-size: 1.625rem;
  }

  .boat-data-header-left .passengers-content,
  .boat-data-header-left .date-content {
    font-size: 1.375rem;
  }
}

@media (max-width: 1110px) {
  .boat-data-header-top {
    flex-direction: column;
  }

  .boat-data-header-left, .boat-data-header-right {
    width: 100%;
  }

  .boat-data-header-left .boat-name {
    justify-content: space-between;
  }

  .data-iconboxes img {
    height: 28px;
  }
}

@media (max-width: 1055px ) {
  .boat-data-header-info{
    overflow-x: auto;
    padding-left: 0!important;
    padding-right: 0!important;
  }
}

@media (max-width: 985px) {
  .extras-price {
    font-size: .875rem;
  }

  :deep(.popup-image) {
    height: 45vh !important;
  }
}

@media (max-width: 768px) {
  .img-dp{
    display: none;
  }
  .c-inside{
    padding-bottom: 15px!important;
  }
  
  .boat-data-header-info{
    overflow-x: auto;
    padding-left: 0!important;
    padding-right: 0!important;
  }

  .boat-data-header-sticky-small {
    z-index: 10;
    box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.1);
  }

  .boat-data-fixed {
    display: flex;
  }

  .boat-data-header-mobi {
    display: flex;
  }
  
  .data-iconboxes {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 20px;
  }

  .main-yacht-page-container {
    margin-top: 6.25rem
  }

  .iconbox-container {
    padding-top: 1.25rem !important;
    padding-bottom: 1.25rem !important;
  }
  

  :deep(.el-collapse-item .el-collapse-item__header) {
    font-size: 1rem !important;
    background: #f8f8f8;
  }

  :deep(.el-collapse-item__content) {
    font-size: .875rem !important;
  }
}

@media (max-width: 580px) {
  .v-selection-control {
    margin-top: 10px !important;
  }
}

@media (max-width: 450px) {
  .boat-data-fixed .req__btn {
    font-size: .75rem;
  }

  .map-popup-map {
    width: 95%;
    height: 95%;
    background-color: white;
    border-radius: 10px;
    position: relative;
  }

  .boat-data-fixed .date-passengers__text {
    font-size: .75rem;
  }

  .boat-data-fixed .price {
    font-size: .875rem;
  }
}


/* we will explain what these classes do next! */
.v-enter-active,
.v-leave-active {
  transition: opacity 0.3s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

:deep(.v-field__input input) {
  font-size: 24px !important;
}

:deep(.v-select__menu-icon) {
  color: black !important;
}


:deep(.swiper-pagination) {
  text-align:start;
  margin-left: 5px;
  margin-bottom: 10px;
}
:deep(.swiper-pagination-bullet-active) {
  background: white;
}
:deep(.swiper-pagination-bullet){
  background: white;
}


:deep(.swiper-button-prev),
:deep(.swiper-button-next) {
  width: 35px ;
  height: 35px;
  background-color: white ;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.swiper-button-prev:after),
:deep(.swiper-button-next:after) {
  font-size: 16px ;
  color: black;
  font-weight: bold;
}

:deep(.swiper-button-prev) {
  left: 10px;
}

:deep(.swiper-button-next) {
  right: 10px;
}

</style>

